import {
  LdtStatusFilterValue,
  createOption,
} from '@ldt/features/orders/statuses/workflow_shared';
import { OrderStatus } from 'src/types';
import type { OrderStatusFilterOption } from 'src/types/status_filter';
import {
  isClinicAdmin,
  isCustomerSuccessOrSalesUser,
  isOrderingPhysician,
  isLabTech,
} from 'src/utils';

/** Direct Upload Lab Tech: statuses shown as "Slide Image Needed". */
const DIRECT_UPLOAD_LAB_TECH_SLIDE_IMAGE_NEEDED_STATUSES: OrderStatus[] = [
  OrderStatus.LabProcessing,
  OrderStatus.AiError,
];

/** Direct Upload Lab: CS/Sales "Processing" filter statuses. */
const DIRECT_UPLOAD_CS_PROCESSING_STATUSES: OrderStatus[] = [
  OrderStatus.AiProcessing,
  OrderStatus.AiError,
  OrderStatus.ReportPending,
  OrderStatus.ReportFaxPending,
  OrderStatus.ReportFaxFailed,
  OrderStatus.PathologyFaxPending,
  OrderStatus.PathologyFaxFailed,
];

/** Direct Upload Lab: Ordering Physician / Clinic Admin "Processing" filter statuses. */
const DIRECT_UPLOAD_OP_CA_PROCESSING_STATUSES: OrderStatus[] = [
  OrderStatus.QualityControlApproved,
  OrderStatus.PathologyFaxPending,
  OrderStatus.PathologyFaxFailed,
  OrderStatus.LabProcessing,
  OrderStatus.LabError,
  OrderStatus.AiProcessing,
  OrderStatus.AiError,
  OrderStatus.ReportPending,
  OrderStatus.ReportRejected,
];

export function getStatusFilterOptions(): Map<string, OrderStatusFilterOption> {
  if (isLabTech()) {
    return new Map<string, OrderStatusFilterOption>([
      createOption(LdtStatusFilterValue.ALL_ORDERS, 'All Orders', [
        ...DIRECT_UPLOAD_LAB_TECH_SLIDE_IMAGE_NEEDED_STATUSES,
        OrderStatus.SlideImageQualityControlFailed,
        OrderStatus.ReportReady,
        OrderStatus.ReportFaxed,
      ]),
      createOption(
        LdtStatusFilterValue.IMAGE_PENDING,
        'Slide Image Needed',
        DIRECT_UPLOAD_LAB_TECH_SLIDE_IMAGE_NEEDED_STATUSES,
      ),
      createOption(LdtStatusFilterValue.IMAGE_QC_FAILED, 'Image QC Failed', [
        OrderStatus.SlideImageQualityControlFailed,
      ]),
      createOption(LdtStatusFilterValue.REPORT_READY, 'Report Released', [
        OrderStatus.ReportReady,
        OrderStatus.ReportFaxed,
      ]),
    ]);
  }

  const isCSOrSalesUser = isCustomerSuccessOrSalesUser();
  const isOrderingPhysicianOrClinicAdmin =
    isOrderingPhysician() || isClinicAdmin();

  if (isCSOrSalesUser) {
    return new Map<string, OrderStatusFilterOption>([
      createOption(LdtStatusFilterValue.ALL_ORDERS, 'All Orders'),
      createOption(LdtStatusFilterValue.REPORT_READY, 'Report Ready', [
        OrderStatus.ReportReady,
      ]),
      createOption(
        LdtStatusFilterValue.PROCESSING,
        'Processing',
        DIRECT_UPLOAD_CS_PROCESSING_STATUSES,
      ),
      createOption(LdtStatusFilterValue.QC_PENDING, 'QC Pending', [
        OrderStatus.QualityControlPending,
      ]),
    ]);
  }

  if (isOrderingPhysicianOrClinicAdmin) {
    return new Map<string, OrderStatusFilterOption>([
      createOption(LdtStatusFilterValue.ALL_ORDERS, 'All Orders'),
      createOption(LdtStatusFilterValue.REPORT_READY, 'Report Released', [
        OrderStatus.ReportReady,
      ]),
      createOption(
        LdtStatusFilterValue.REPORT_FAX_PENDING,
        'Report Fax Pending',
        [OrderStatus.ReportFaxFailed, OrderStatus.ReportFaxPending],
      ),
      createOption(LdtStatusFilterValue.REPORT_FAXED, 'Report Faxed', [
        OrderStatus.ReportFaxed,
      ]),
      createOption(
        LdtStatusFilterValue.PROCESSING,
        'Processing',
        DIRECT_UPLOAD_OP_CA_PROCESSING_STATUSES,
      ),
      createOption(LdtStatusFilterValue.QC_PENDING, 'Order Created', [
        OrderStatus.QualityControlPending,
      ]),
    ]);
  }

  throw new Error('Invalid user role');
}
